<template>
  <div v-if="shouldShowModeInfo" class="w-full">
    <!-- 動態顯示標題 -->
    <div class="consumption-mode-header">
      <!-- 標題區域 -->
      <div class="title" v-if="consumptionMethod === 'dineIn' && tableName">
        <span class="text-primary-color">門市內用</span><span class="black-text">/{{ tableName }}</span>
      </div>
      <div class="title text-primary-color" v-else>{{ modeTitle }}</div>
      
      <!-- 組件區域 -->
      <component 
        ref="modeEditComponent"
        :is="modeComponent" 
        v-if="modeComponent"
        :editMode="editMode"
      />
      
      <!-- 發票選項區域（移入容器內，添加分隔線） -->
      <div v-if="shouldShowInvoiceSelector" class="invoice-section">
        <div class="divider"></div>
        <InvoiceSelector 
          ref="invoiceSelectorRef"
          :showConfirmButton="true"
          :syncToMember="syncInvoiceToMember"
          @invoice-updated="handleInvoiceUpdated"
          @validation-error="handleValidationError"
        />
        
        <!-- 預約自取提示訊息（顯示在發票資訊下方，使用原本樣式） -->
        <div v-if="consumptionMethod === 'pickUp'" class="column_center_start w-full flex-wrap mt-5">
          <text class="font-base">取餐時，請提供自取號碼。</text>
          <text class="font-base">付款後，依現場排序，完成取餐。</text>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import pickUpEdit from './pickUpEdit.vue'
import deliveryEdit from './deliveryEdit.vue'
import takeOutEdit from './takeOutEdit.vue'
import InvoiceSelector from './InvoiceSelector.vue'
import commonUtilities from "@/utils/common.utils"

export default {
  name: 'ConsumptionModeInfo',
  components: {
    pickUpEdit,
    deliveryEdit,
    takeOutEdit,
    InvoiceSelector
  },
  props: {
    // 消費模式：'dineIn', 'takeOut', 'pickUp', 'delivery', 'retailPickUp', 'retailDelivery'
    consumptionMethod: {
      type: String,
      required: true,
      validator: (value) => ['dineIn', 'takeOut', 'pickUp', 'delivery', 'retailPickUp', 'retailDelivery'].includes(value)
    },
    // 是否為編輯模式
    editMode: {
      type: Boolean,
      default: false
    },
    // 是否同步發票資料到會員資料
    syncInvoiceToMember: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    // 需要存取 Vuex store 中的桌號資訊
    tableName() {
      return this.$store.getters['cart/tableName']
    },
    
    // 動態標題邏輯
    modeTitle() {
      const titles = {
        'dineIn': this.tableName ? `門市內用/${this.tableName}` : '門市內用',
        'takeOut': '現場外帶',
        'pickUp': '預約自取',
        'delivery': '店家外送',
        'retailPickUp': '零售自取',
        'retailDelivery': '零售宅配'
      }
      return titles[this.consumptionMethod] || ''
    },
    
    // 消費模式配置映射
    modeConfig() {
      const configs = {
        'dineIn': {
          component: null // 門市內用不需要額外組件
        },
        'takeOut': {
          component: 'takeOutEdit'
        },
        'pickUp': {
          component: 'pickUpEdit' // 使用編輯組件
        },
        'delivery': {
          component: 'deliveryEdit' // 使用編輯組件
        },
        'retailPickUp': {
          component: 'pickUpEdit'
        },
        'retailDelivery': {
          component: 'deliveryEdit'
        }
      }
      return configs[this.consumptionMethod] || null
    },
    
    // 是否應該顯示消費模式資訊
    shouldShowModeInfo() {
      return this.modeConfig !== null
    },
    
    // 對應的組件名稱
    modeComponent() {
      return this.modeConfig?.component || null
    },
    
    // 需求 4.3: 判斷是否顯示發票選項
    // 使用 enableInvoiceSelector getter 從 basic.module.js
    shouldShowInvoiceSelector() {
      return this.$store.getters['basic/enableInvoiceSelector'] ?? false
    },
    
    // 需求 13.6: 監聽 Vuex store 發票資料變化
    invoiceData() {
      return {
        invoiceTypeIndex: this.$store.getters['cart/invoiceTypeIndex'] ?? 0,
        invoiceCarrier: this.$store.getters['cart/invoiceCarrier'] ?? null,
        invoiceVatNumber: this.$store.getters['cart/invoiceVatNumber'] ?? null,
        invoiceNpoBan: this.$store.getters['cart/invoiceNpoBan'] ?? null
      }
    }
  },
  methods: {
    // 儲存外送地址（如果 checkbox 已勾選）
    async saveDeliveryAddressIfChecked() {
      const component = this.$refs.modeEditComponent
      if (!component) return
      
      // 只有 deliveryEdit 組件才有 saveIfChecked 方法
      if (['delivery', 'retailDelivery'].includes(this.consumptionMethod) && typeof component.saveIfChecked === 'function') {
        await component.saveIfChecked()
      }
    },
    
    // 驗證外送資訊（供父組件呼叫）
    validateDeliveryInfo() {
      const component = this.$refs.modeEditComponent
      if (!component) {
        commonUtilities.createToast("error", "無法取得外送資訊組件")
        return false
      }
      
      // 只有 deliveryEdit 組件才有 validateAllFields 方法
      if (['delivery', 'retailDelivery'].includes(this.consumptionMethod) && typeof component.validateAllFields === 'function') {
        return component.validateAllFields()
      }
      
      return true
    },

    // 驗證現場外帶資訊（供父組件呼叫）
    validateTakeOutInfo() {
      const component = this.$refs.modeEditComponent
      if (!component) return true

      if (this.consumptionMethod === 'takeOut' && typeof component.validateAllFields === 'function') {
        return component.validateAllFields()
      }

      return true
    },
    
    // 需求 4.5: 處理發票資料更新事件
    handleInvoiceUpdated(invoiceData) {
      console.log('ConsumptionModeInfo: 發票資料已更新', invoiceData)
      // 發票資料已經在 InvoiceSelector 組件中更新到 Vuex store
      // 這裡只需要記錄日誌，不需要額外處理
    },
    
    // 處理發票驗證錯誤事件
    handleValidationError(errorMessage) {
      console.error('ConsumptionModeInfo: 發票驗證錯誤', errorMessage)
      // 錯誤訊息已經在 InvoiceSelector 組件中顯示
      // 這裡只需要記錄日誌
    }
  }
}
</script>

<style lang="scss" scoped>
// 消費模式標題容器
.consumption-mode-header {
  padding: 20px 16px;
  background-color: #F5F5F5; // 灰色背景
  border-radius: 8px;
  display: flex;
  flex-direction: column; // 垂直排列
  align-items: center; // 水平置中
  gap: 12px; // 標題和組件之間的間距
}

.title {
  font-size: 24px;
  font-weight: 600;
  font-family: PingFang SC;
  text-align: center;
  margin-bottom: 0; // 移除預設 margin，使用 gap 控制間距
  
  .black-text {
    color: #000; // 黑色文字（桌號部分）
  }
}

// 需求 4.4: 發票選項區域樣式（移入容器內）
.invoice-section {
  width: 100%;
  margin-top: 0; // 移除 margin-top，因為已在容器內
  padding: 0;
  background-color: transparent; // 改為透明，繼承容器背景
}

// 分隔線樣式
.divider {
  width: 100%;
  height: 1px;
  background-color: #DDDDDD; // 淺灰色分隔線
  margin: 16px 0; // 上下間距
}

// Flex 工具類別
.row_center_between {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
</style>
